import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { HowItWorksBlock } from "../../components/HowItWorksBlock";
import { useTranslation } from "../../lang/TranslationContext";

export const HowItWorks = () => {
  const { translations } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const totalSlides = 3;

  const next = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, []);

  const previous = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      next();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, next]);

  const handleSlideClick = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleNavClick = (direction: "prev" | "next") => {
    if (direction === "prev") previous();
    else next();
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <Wrap>
      <Header>
        <h1>{translations.landing.howItWorks.heading}</h1>
      </Header>

      <SliderContainer>
        <SliderTrack $offset={currentSlide}>
          <HowItWorksBlock step={0} />
          <HowItWorksBlock step={1} />
          <HowItWorksBlock step={2} />
        </SliderTrack>
      </SliderContainer>

      <Controls>
        <DotsContainer>
          {[0, 1, 2].map((index) => (
            <Dot
              key={index}
              $active={currentSlide === index}
              onClick={() => handleSlideClick(index)}
            />
          ))}
        </DotsContainer>

        <NavigationControls>
          <ArrowButton onClick={() => handleNavClick("prev")}>
            <i className="pi pi-chevron-left" />
          </ArrowButton>
          <ArrowButton onClick={() => handleNavClick("next")}>
            <i className="pi pi-chevron-right" />
          </ArrowButton>
        </NavigationControls>
      </Controls>
    </Wrap>
  );
};

const SliderContainer = styled.div`
  margin: 30px 0;
  width: 100%;
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const SliderTrack = styled.div<{ $offset: number }>`
  display: flex;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(-${({ $offset }) => $offset * 100}%);
  height: 100%;

  > * {
    flex-shrink: 0;
    width: 100%;
    height: 100%;
  }
`;

const Header = styled.div`
  padding: 100px 20px 0;

  h1 {
    font-size: 48px;
    font-weight: 600;
    color: #ffffff;
    letter-spacing: -0.03em;
    text-align: center;
  }

  @media screen and (max-width: 768px) {
    top: 32px;

    h1 {
      font-size: 32px;
    }
  }

  @media screen and (max-width: 440px) {
    top: 24px;

    h1 {
      font-size: 24px;
    }
  }
`;

const Controls = styled.div`
  position: absolute;
  bottom: 48px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 48px;
  z-index: 10;

  @media screen and (max-width: 768px) {
    bottom: 32px;
    gap: 32px;
  }

  @media screen and (max-width: 440px) {
    bottom: 24px;
    gap: 24px;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? "32px" : "12px")};
  height: 12px;
  border-radius: 6px;
  border: none;
  background: ${({ $active }) =>
    $active ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.3)"};
  cursor: pointer;
  transition: all 0.4s ease;
  padding: 0;

  &:hover {
    background: ${({ $active }) =>
      $active ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.5)"};
  }
`;

const NavigationControls = styled.div`
  display: flex;
  gap: 12px;
`;

const Wrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background: linear-gradient(135deg, #141d1b 0%, #152320 50%, #0f2c25 100%);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      ellipse at 50% 50%,
      rgba(26, 95, 79, 0.15) 0%,
      transparent 50%
    );
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 100%
    );
  }
`;

const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(12px);

  i {
    font-size: 18px;
    color: #ffffff;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.35);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }

  @media screen and (max-width: 768px) {
    width: 48px;
    height: 48px;

    i {
      font-size: 16px;
    }
  }
`;
